<template>
	<div>
		<label
			:for="id"
			class="mb-2 font-heading text-base font-extrabold text-grey-950"
			>{{ label
			}}<span v-if="required" class="ml-1 text-error">*</span></label
		>
		<div
			class="flex min-h-[44px] overflow-hidden rounded-lg border border-grey-300 bg-white focus-within:ring-2 focus-within:ring-grey-300"
		>
			<input
				:id="id"
				v-model="pending"
				type="email"
				:disabled="disabled"
				:placeholder="$t('email-templates.address-placeholder')"
				class="min-w-0 flex-1 border-0 px-3 py-2 text-base text-grey-800 placeholder-grey-700 focus:outline-none disabled:placeholder:text-grey-400"
				:aria-describedby="describedBy"
				:aria-invalid="String(!!shownError)"
				@keydown.enter.prevent="add"
				@keydown.,.prevent="add"
				@blur="add"
			/>
			<Button
				type="secondary"
				class="h-auto shrink-0 rounded-none border-y-0 border-l border-r-0 border-grey-300 bg-grey-50 px-3.5 text-grey-1000 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-grey-500"
				:disabled="disabled || !pending || value.length >= limit"
				@click="add"
				>{{ $t('email-templates.add') }}</Button
			>
		</div>
		<p
			v-if="shownError"
			:id="id + '-error'"
			role="alert"
			class="mb-0 mt-2 text-xs text-error"
		>
			{{ shownError }}
		</p>
		<p :id="id + '-help'" class="mb-0 mt-2 text-xs text-grey-600">
			{{ help }}
		</p>
		<div v-if="value.length" class="mt-3 flex flex-wrap gap-2">
			<span
				v-for="(address, index) in value"
				:key="index"
				class="inline-flex max-w-full items-center gap-2 rounded-[20px] border border-grey-200 bg-grey-100 px-2.5 py-1.5 text-sm text-grey-1000"
			>
				<span class="break-all">{{ address }}</span>
				<button
					type="button"
					:disabled="disabled"
					:aria-label="$t('email-templates.remove') + ' ' + address"
					class="shrink-0 rounded focus-visible:ring-2 focus-visible:ring-grey-500"
					@click="remove(index)"
				>
					<IconTimes :size="12" />
				</button>
			</span>
		</div>
	</div>
</template>
<script>
import Button from '~/components/ui/common/Button'
import IconTimes from '~/components/Icons/IconTimes'
export default {
	components: { Button, IconTimes },
	props: {
		value: { type: Array, default: () => [] },
		id: { type: String, required: true },
		label: String,
		help: String,
		limit: { type: Number, default: Infinity },
		required: Boolean,
		disabled: Boolean,
		error: { type: String, default: '' }
	},
	data: () => ({ pending: '', invalid: false, errorKey: '' }),
	computed: {
		shownError() {
			if (!this.invalid) return this.error
			const message = this.$t(this.errorKey)
			return message === 'email-templates.duplicate-email'
				? 'This email address has already been added.'
				: message
		},
		describedBy() {
			return this.shownError
				? this.id + '-help ' + this.id + '-error'
				: this.id + '-help'
		}
	},
	watch: {
		pending(value) {
			this.$emit('pending', value)
			if (this.invalid && !this.acceptanceError(this.value))
				this.clearError()
		}
	},
	methods: {
		acceptanceError(existing) {
			if (!this.pending.trim()) return ''
			const addresses = this.pending
				.split(/[,;]/)
				.map(x => x.trim())
				.filter(Boolean)
			const known = existing.map(address => address.toLowerCase())
			if (
				addresses.some(x => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(x))
			)
				return 'email-templates.invalid-email'
			if (
				addresses.some((address, index) => {
					const key = address.toLowerCase()
					return (
						known.includes(key) ||
						addresses.findIndex(
							other => other.toLowerCase() === key
						) !== index
					)
				})
			)
				return 'email-templates.duplicate-email'
			if (existing.length + addresses.length > this.limit)
				return 'email-templates.bcc-limit'
			return ''
		},
		add() {
			if (this.disabled) return false
			this.errorKey = this.acceptanceError(this.value)
			this.invalid = !!this.errorKey
			if (this.invalid) return false
			if (!this.pending.trim()) return true
			const addresses = this.pending
				.split(/[,;]/)
				.map(x => x.trim())
				.filter(Boolean)
			this.$emit('input', [...this.value, ...addresses])
			this.pending = ''
			return true
		},
		clearError() {
			this.invalid = false
			this.errorKey = ''
		},
		remove(index) {
			if (this.disabled) return
			const remaining = this.value.filter((x, i) => i !== index)
			this.$emit('input', remaining)
			if (this.invalid && !this.acceptanceError(remaining))
				this.clearError()
		}
	}
}
</script>
